import { type CSSProperties, useState, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { PageHeader, EmptyState, ErrorState, StatusPill } from '@ui-kit';
import { useCallHistory } from '../hooks/useCallHistory';
import { CallLogDetailDrawer } from '../components/CallLogDetailDrawer';
import { IntegrationCommonUsagePanel } from '../components/IntegrationCommonUsagePanel';
import type { IntegrationProtocol, CallResult, ApiError, CallLogFilter } from '../types/integrationHub';

const DEFAULT_PAGE_SIZE = 20;

function SkeletonRow({ cols }: { cols: number }) {
  return (
    <tr>
      {Array.from({ length: cols }).map((_, i) => (
        <td
          key={i}
          style={{
            padding: 'var(--spacing-3) var(--spacing-4)',
            borderBottom: '1px solid var(--color-surface-border)',
          }}
        >
          <div
            style={{
              height: 12,
              width: `${50 + (i * 11) % 40}%`,
              borderRadius: 'var(--radius-sm)',
              background: 'var(--color-surface-elevated)',
              animation: 'pulse 1.5s ease-in-out infinite',
            }}
          />
        </td>
      ))}
    </tr>
  );
}

/**
 * CallHistoryPage — 연동 호출 이력 검색 + 상세 drawer (/integration-hub/history)
 * 5상태: loading / empty / error / default / dialog(CallLogDetailDrawer)
 */
export function CallHistoryPage() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();

  // 필터 상태
  const [endpointCode, setEndpointCode] = useState('');
  const [protocol, setProtocol] = useState<IntegrationProtocol | ''>('');
  const [result, setResult] = useState<CallResult | ''>('');
  const [minStatus, setMinStatus] = useState('');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [traceId, setTraceId] = useState('');
  const [page, setPage] = useState(0);

  const [appliedFilters, setAppliedFilters] = useState<CallLogFilter>({
    page: 0,
    size: DEFAULT_PAGE_SIZE,
    sort: 'calledAt,desc',
  });

  // drawer 상태
  const [selectedLogId, setSelectedLogId] = useState<number | null>(null);

  const historyQuery = useCallHistory(appliedFilters);

  const pageData = historyQuery.data;
  const isLoading = historyQuery.isLoading;
  const isError = historyQuery.isError;
  const isEmpty = !isLoading && !isError && (pageData?.empty ?? false);
  const showTable = !isLoading && !isError && !isEmpty && !!pageData;
  const is403 = isError && (historyQuery.error as unknown as ApiError)?.code === 'AUTH-403-001';

  const handleSearch = useCallback(() => {
    setPage(0);
    setAppliedFilters({
      endpointCode: endpointCode || undefined,
      protocol: protocol || undefined,
      result: result || undefined,
      minStatus: minStatus ? Number(minStatus) : undefined,
      from: from || undefined,
      to: to || undefined,
      traceId: traceId || undefined,
      page: 0,
      size: DEFAULT_PAGE_SIZE,
      sort: 'calledAt,desc',
    });
  }, [endpointCode, protocol, result, minStatus, from, to, traceId]);

  const handleReset = useCallback(() => {
    setEndpointCode('');
    setProtocol('');
    setResult('');
    setMinStatus('');
    setFrom('');
    setTo('');
    setTraceId('');
    setPage(0);
    setAppliedFilters({ page: 0, size: DEFAULT_PAGE_SIZE, sort: 'calledAt,desc' });
  }, []);

  const handlePageChange = useCallback((newPage: number) => {
    setPage(newPage);
    setAppliedFilters((prev) => ({ ...prev, page: newPage }));
  }, []);

  const TABLE_HEADERS = useMemo(
    () => [
      t('integrationHub.history.column.calledAt'),
      t('integrationHub.history.column.endpoint'),
      t('integrationHub.history.column.operation'),
      t('integrationHub.history.column.status'),
      t('integrationHub.history.column.duration'),
      t('integrationHub.history.column.result'),
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [t, i18n.language]
  );

  return (
    <div style={{ padding: 0 }}>
      <PageHeader
        title={t('integrationHub.history.title')}
        breadcrumb={<span>{t('integrationHub.history.breadcrumb')}</span>}
        actions={
          <div style={{ display: 'flex', gap: 'var(--spacing-2)' }}>
            <button type="button" onClick={() => navigate('/integration-hub')} style={tabButtonStyle}>
              {t('integrationHub.tab.endpoints')}
            </button>
            <button type="button" onClick={() => navigate('/integration-hub/storage')} style={tabButtonStyle}>
              {t('integrationHub.tab.storage')}
            </button>
            <button type="button" onClick={() => navigate('/integration-hub/tcp')} style={tabButtonStyle}>
              {t('integrationHub.tab.tcp')}
            </button>
            <button type="button" onClick={() => navigate('/integration-hub/grpc')} style={tabButtonStyle}>
              {t('integrationHub.tab.grpc')}
            </button>
          </div>
        }
      />

      {/* 필터 바 */}
      <div
        role="search"
        aria-label={t('integrationHub.history.title')}
        style={filterBarStyle}
      >
        {/* 엔드포인트 코드 */}
        <label style={filterLabelStyle}>
          {t('integrationHub.history.filter.endpoint')}
          <input
            type="text"
            value={endpointCode}
            placeholder={t('integrationHub.history.filter.placeholder.endpoint')}
            onChange={(e) => setEndpointCode(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') handleSearch(); }}
            style={inputStyle}
          />
        </label>

        {/* 프로토콜 */}
        <label style={filterLabelStyle}>
          {t('integrationHub.history.filter.protocol')}
          <select
            value={protocol}
            onChange={(e) => setProtocol(e.target.value as IntegrationProtocol | '')}
            style={selectStyle}
          >
            <option value="">{t('integrationHub.history.filter.all')}</option>
            <option value="HTTP">{t('integrationHub.protocol.HTTP')}</option>
            <option value="S3">{t('integrationHub.protocol.S3')}</option>
            <option value="SFTP">{t('integrationHub.protocol.SFTP')}</option>
          </select>
        </label>

        {/* 결과 */}
        <label style={filterLabelStyle}>
          {t('integrationHub.history.filter.result')}
          <select
            value={result}
            onChange={(e) => setResult(e.target.value as CallResult | '')}
            style={selectStyle}
          >
            <option value="">{t('integrationHub.history.filter.all')}</option>
            <option value="SUCCESS">{t('integrationHub.result.SUCCESS')}</option>
            <option value="ERROR">{t('integrationHub.result.ERROR')}</option>
          </select>
        </label>

        {/* 상태 이상 */}
        <label style={filterLabelStyle}>
          {t('integrationHub.history.filter.minStatus')}
          <input
            type="number"
            value={minStatus}
            placeholder={t('integrationHub.history.filter.placeholder.minStatus')}
            min={100}
            max={599}
            onChange={(e) => setMinStatus(e.target.value)}
            style={{ ...inputStyle, width: 90 }}
          />
        </label>

        {/* 기간 */}
        <label style={filterLabelStyle}>
          {t('integrationHub.history.filter.from')}
          <input
            type="datetime-local"
            value={from}
            onChange={(e) => setFrom(e.target.value ? new Date(e.target.value).toISOString() : '')}
            style={inputStyle}
          />
        </label>
        <label style={filterLabelStyle}>
          {t('integrationHub.history.filter.to')}
          <input
            type="datetime-local"
            value={to}
            onChange={(e) => setTo(e.target.value ? new Date(e.target.value).toISOString() : '')}
            style={inputStyle}
          />
        </label>

        {/* Trace ID */}
        <label style={filterLabelStyle}>
          {t('integrationHub.history.filter.traceId')}
          <input
            type="text"
            value={traceId}
            placeholder={t('integrationHub.history.filter.placeholder.traceId')}
            maxLength={32}
            onChange={(e) => setTraceId(e.target.value)}
            style={{ ...inputStyle, width: 160 }}
          />
        </label>

        {/* 버튼 */}
        <div style={{ display: 'flex', gap: 'var(--spacing-2)', marginLeft: 'auto', alignSelf: 'flex-end' }}>
          <button type="button" onClick={handleSearch} disabled={isLoading} style={primaryButtonStyle}>
            {t('integrationHub.history.button.search')}
          </button>
          <button type="button" onClick={handleReset} style={secondaryButtonStyle}>
            {t('integrationHub.history.button.reset')}
          </button>
        </div>
      </div>

      {/* 5상태 콘텐츠 */}
      <div
        style={{
          border: '1px solid var(--color-surface-border)',
          borderRadius: 'var(--radius-md)',
          overflow: 'hidden',
          background: 'var(--color-surface-background)',
        }}
      >
        {/* loading skeleton */}
        {isLoading && (
          <table
            style={{ width: '100%', borderCollapse: 'collapse', fontSize: 'var(--font-size-sm)' }}
            aria-label={t('integrationHub.history.loadingAriaLabel')}
            aria-busy="true"
          >
            <thead>
              <tr style={{ background: 'var(--color-surface-muted)' }}>
                {TABLE_HEADERS.map((h) => (
                  <th key={h} style={thStyle}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {Array.from({ length: 5 }).map((_, i) => (
                <SkeletonRow key={i} cols={TABLE_HEADERS.length} />
              ))}
            </tbody>
          </table>
        )}

        {/* error */}
        {isError && (
          <div style={{ padding: 'var(--spacing-6)' }}>
            {is403 ? (
              <ErrorState
                title={t('integrationHub.error.AUTH-403-001')}
                message={t('integrationHub.error.forbidden')}
                onRetry={() => navigate('/')}
              />
            ) : (
              <ErrorState
                title={t('integrationHub.error.loadFailed')}
                message={(historyQuery.error as unknown as ApiError)?.message ?? t('integrationHub.error.network')}
                onRetry={() => void historyQuery.refetch()}
              />
            )}
          </div>
        )}

        {/* empty */}
        {isEmpty && (
          <div style={{ padding: 'var(--spacing-6)' }}>
            <EmptyState
              title={t('integrationHub.history.empty.title')}
              description={t('integrationHub.history.empty.description')}
              action={
                <button type="button" onClick={handleReset} style={secondaryButtonStyle}>
                  {t('integrationHub.history.button.reset')}
                </button>
              }
            />
          </div>
        )}

        {/* default — DataTable */}
        {showTable && pageData && (
          <>
            {pageData.totalElements > 0 && (
              <p style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-text-secondary)', padding: 'var(--spacing-2) var(--spacing-4)', margin: 0 }}>
                {t('integrationHub.history.totalCount', { count: pageData.totalElements })}
              </p>
            )}
            <div role="region" aria-label={t('integrationHub.history.tableAriaLabel')} style={{ overflowX: 'auto' }}>
              <table
                style={{ width: '100%', borderCollapse: 'collapse', fontSize: 'var(--font-size-sm)' }}
                aria-label={t('integrationHub.history.tableAriaLabel')}
              >
                <thead>
                  <tr style={{ background: 'var(--color-surface-muted)' }}>
                    {TABLE_HEADERS.map((h) => (
                      <th key={h} style={thStyle}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {pageData.content.map((row) => (
                    <tr
                      key={row.id}
                      tabIndex={0}
                      role="button"
                      aria-label={`이력 ${row.id} 상세 보기`}
                      onClick={() => setSelectedLogId(row.id)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                          setSelectedLogId(row.id);
                        }
                      }}
                      style={{ cursor: 'pointer', transition: 'background 0.1s' }}
                      onMouseEnter={(e) => {
                        (e.currentTarget as HTMLTableRowElement).style.background = 'var(--color-surface-muted)';
                      }}
                      onMouseLeave={(e) => {
                        (e.currentTarget as HTMLTableRowElement).style.background = '';
                      }}
                    >
                      {/* 시각 */}
                      <td style={{ ...tdStyle, whiteSpace: 'nowrap', fontFamily: 'var(--font-family-mono)', fontSize: 'var(--font-size-xs)' }}>
                        {formatDate(row.calledAt)}
                      </td>
                      {/* 엔드포인트 */}
                      <td style={{ ...tdStyle, fontFamily: 'var(--font-family-mono)', fontSize: 'var(--font-size-xs)', maxWidth: 160, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {row.endpointCode ?? '-'}
                      </td>
                      {/* 작업 */}
                      <td style={tdStyle}>
                        {row.operation
                          ? t(`integrationHub.operation.${row.operation}`, { defaultValue: row.operation })
                          : '-'}
                      </td>
                      {/* HTTP 상태 */}
                      <td style={{ ...tdStyle, fontFamily: 'var(--font-family-mono)', fontWeight: 700 }}>
                        {row.responseStatus != null ? (
                          <span style={{ color: row.responseStatus >= 400 ? 'var(--color-danger)' : 'var(--color-success)' }}>
                            {row.responseStatus}
                          </span>
                        ) : '-'}
                      </td>
                      {/* 지연 */}
                      <td style={{ ...tdStyle, textAlign: 'right', fontFamily: 'var(--font-family-mono)' }}>
                        {row.durationMs != null ? row.durationMs : '-'}
                      </td>
                      {/* 결과 */}
                      <td style={tdStyle}>
                        <StatusPill tone={row.result === 'SUCCESS' ? 'success' : 'danger'}>
                          {t(`integrationHub.result.${row.result}`)}
                        </StatusPill>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* 페이지네이션 */}
            <div style={paginationBarStyle}>
              <span style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-text-secondary)' }}>
                {t('integrationHub.history.pagination.showing', {
                  total: pageData.totalElements,
                  from: pageData.number * pageData.size + 1,
                  to: Math.min((pageData.number + 1) * pageData.size, pageData.totalElements),
                })}
              </span>
              <nav aria-label={t('integrationHub.history.pagination.nav')} style={{ display: 'inline-flex', gap: 'var(--spacing-1)' }}>
                <button
                  type="button"
                  disabled={pageData.first}
                  onClick={() => handlePageChange(page - 1)}
                  aria-label={t('integrationHub.history.pagination.prev')}
                  style={paginationButtonStyle(pageData.first)}
                >
                  &lt;
                </button>
                {Array.from({ length: Math.min(pageData.totalPages, 10) }).map((_, i) => (
                  <button
                    key={i}
                    type="button"
                    onClick={() => handlePageChange(i)}
                    aria-label={t('integrationHub.history.pagination.page', { page: i + 1 })}
                    aria-current={i === pageData.number ? 'page' : undefined}
                    style={paginationPageStyle(i === pageData.number)}
                  >
                    {i + 1}
                  </button>
                ))}
                <button
                  type="button"
                  disabled={pageData.last}
                  onClick={() => handlePageChange(page + 1)}
                  aria-label={t('integrationHub.history.pagination.next')}
                  style={paginationButtonStyle(pageData.last)}
                >
                  &gt;
                </button>
              </nav>
            </div>
          </>
        )}
      </div>

      {/* 공통 모듈 패널 */}
      <IntegrationCommonUsagePanel />

      {/* CallLogDetailDrawer (dialog 상태) */}
      {selectedLogId !== null && (
        <CallLogDetailDrawer
          logId={selectedLogId}
          onClose={() => setSelectedLogId(null)}
        />
      )}

      <style>{`
        @keyframes pulse {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.4; }
        }
        @media (max-width: 768px) {
          table { min-width: 700px; }
        }
      `}</style>
    </div>
  );
}

function formatDate(iso: string): string {
  try {
    return new Date(iso).toLocaleString('ko-KR', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return iso;
  }
}

// Shared styles
const tabButtonStyle: CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  padding: 'var(--spacing-2) var(--spacing-3)',
  background: 'var(--color-surface-elevated)',
  color: 'var(--color-text-primary)',
  border: '1px solid var(--color-surface-border)',
  borderRadius: 'var(--radius-md)',
  fontSize: 'var(--font-size-sm)',
  fontWeight: 500,
  cursor: 'pointer',
  fontFamily: 'var(--font-family-sans)',
};

const primaryButtonStyle: CSSProperties = {
  padding: 'var(--spacing-2) var(--spacing-4)',
  background: 'var(--color-brand-primary)',
  color: 'var(--color-text-inverse)',
  border: 'none',
  borderRadius: 'var(--radius-md)',
  fontSize: 'var(--font-size-sm)',
  fontWeight: 600,
  cursor: 'pointer',
  fontFamily: 'var(--font-family-sans)',
};

const secondaryButtonStyle: CSSProperties = {
  padding: 'var(--spacing-2) var(--spacing-4)',
  background: 'var(--color-surface-elevated)',
  color: 'var(--color-text-primary)',
  border: '1px solid var(--color-surface-border)',
  borderRadius: 'var(--radius-md)',
  fontSize: 'var(--font-size-sm)',
  cursor: 'pointer',
  fontFamily: 'var(--font-family-sans)',
};

const filterBarStyle: CSSProperties = {
  display: 'flex',
  flexWrap: 'wrap',
  gap: 'var(--spacing-3)',
  padding: 'var(--spacing-3) var(--spacing-4)',
  border: '1px solid var(--color-surface-border)',
  borderRadius: 'var(--radius-md)',
  background: 'var(--color-surface-muted)',
  marginBottom: 'var(--spacing-4)',
  alignItems: 'flex-end',
};

const filterLabelStyle: CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: 'var(--spacing-1)',
  fontSize: 'var(--font-size-xs)',
  color: 'var(--color-text-secondary)',
};

const inputStyle: CSSProperties = {
  padding: 'var(--spacing-2) var(--spacing-3)',
  border: '1px solid var(--color-surface-border)',
  borderRadius: 'var(--radius-md)',
  fontSize: 'var(--font-size-sm)',
  background: 'var(--color-surface-elevated)',
  color: 'var(--color-text-primary)',
  fontFamily: 'var(--font-family-sans)',
  minWidth: 120,
};

const selectStyle: CSSProperties = {
  padding: 'var(--spacing-2) var(--spacing-3)',
  border: '1px solid var(--color-surface-border)',
  borderRadius: 'var(--radius-md)',
  fontSize: 'var(--font-size-sm)',
  background: 'var(--color-surface-elevated)',
  color: 'var(--color-text-primary)',
  cursor: 'pointer',
};

const thStyle: CSSProperties = {
  textAlign: 'left',
  padding: 'var(--spacing-3) var(--spacing-4)',
  borderBottom: '1px solid var(--color-surface-border)',
  fontSize: 'var(--font-size-xs)',
  color: 'var(--color-text-secondary)',
  fontWeight: 600,
  whiteSpace: 'nowrap',
};

const tdStyle: CSSProperties = {
  padding: 'var(--spacing-3) var(--spacing-4)',
  borderBottom: '1px solid var(--color-surface-border)',
  color: 'var(--color-text-primary)',
};

const paginationBarStyle: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  padding: 'var(--spacing-3) var(--spacing-4)',
  background: 'var(--color-surface-muted)',
  borderTop: '1px solid var(--color-surface-border)',
  flexWrap: 'wrap',
  gap: 'var(--spacing-2)',
};

function paginationButtonStyle(disabled: boolean): CSSProperties {
  return {
    minWidth: 28,
    height: 28,
    padding: '0 var(--spacing-2)',
    borderRadius: 'var(--radius-sm)',
    background: 'var(--color-surface-elevated)',
    border: '1px solid var(--color-surface-border)',
    fontSize: 'var(--font-size-xs)',
    cursor: disabled ? 'not-allowed' : 'pointer',
    opacity: disabled ? 0.5 : 1,
  };
}

function paginationPageStyle(active: boolean): CSSProperties {
  return {
    minWidth: 28,
    height: 28,
    padding: '0 var(--spacing-2)',
    borderRadius: 'var(--radius-sm)',
    background: active ? 'var(--color-brand-primary)' : 'var(--color-surface-elevated)',
    border: `1px solid ${active ? 'var(--color-brand-primary)' : 'var(--color-surface-border)'}`,
    color: active ? 'var(--color-text-inverse)' : 'var(--color-text-secondary)',
    fontSize: 'var(--font-size-xs)',
    cursor: 'pointer',
    fontWeight: active ? 600 : 400,
  };
}
